// Key -> MCP mapping table for the imports order helper. Pure module: no React, no network.

export const ASSOCIATION_ROLES = [
  'Accounting', 'Agent', 'Appraisal Company', 'Assistant', 'Attorney', 'Borrower', 'Builder', 'Buyer',
  'Credit Card', 'Countersigner', 'Escrow Officer', 'Examiner', 'Exchange 1031', 'Funder',
  'General Contractor', 'Government Entity', 'Home Warranty', 'Homeowners Association',
  'Homeowners Association Management', 'Insurance Company', 'Law Firm', 'Legal', 'Lender',
  'Listing Agent', 'Loan Closer', 'Loan Officer', 'Loan Originator', 'Loan Processor', 'Marketer',
  'Mortgage Broker', 'Notary', 'Other', 'Payoff Lender', 'Passthrough', 'Pest Inspector',
  'Policy Production', 'Power of Attorney', 'Provider', 'Real Estate Agent',
  'Real Estate Owned Management', 'Recording Office', 'Release Tracker', 'Representative',
  'Representing', 'Seller', 'Selling Agent', 'Settlement Agent', 'Surveying Firm', 'Spouse',
  'Tax Authority', 'Tax Assessor', 'Title Abstractor', 'Title Insurance Guaranty Association',
  'Transaction Coordinator', 'Underwriter', 'Utility',
] as const;

export const PARTY_KINDS = [
  'Corporation', 'Estate', 'Government', 'Guardianship', 'Legal Trust',
  'Limited Liability Company (LLC)', 'Nonprofit Organization (NPO)', 'Organization', 'Partnership',
  'Person', 'Taxes',
] as const;

export type AssociationRole = (typeof ASSOCIATION_ROLES)[number];
export type PartyKind = (typeof PARTY_KINDS)[number];

export type Coercion = 'string' | 'enum' | 'iso-date' | 'integer' | 'boolean' | 'decimal-0-1' | 'string-list' | 'html-note';

export type CategoryRule = {
  readonly id: string;
  readonly keys: string;
  readonly pattern: RegExp;
  readonly tools: readonly string[];
  readonly required: readonly string[];
  readonly phase: 1 | 2;
  readonly coercion?: Coercion;
  readonly notes?: string;
};

type RuleDef = Omit<CategoryRule, 'phase'>;

const ROLES_BY_LOWERCASE: ReadonlyMap<string, AssociationRole> = new Map(
  ASSOCIATION_ROLES.map((role): [string, AssociationRole] => [role.toLowerCase(), role]),
);

export const normalizeRole = (role: string): AssociationRole | null => (
  ROLES_BY_LOWERCASE.get(role.replace(/[_-]/g, ' ').toLowerCase()) ?? null
);

const phase1 = (def: RuleDef): CategoryRule => ({ ...def, phase: 1 });

const alt = (names: readonly string[]): string => `(?:${names.join('|')})`;

const keyPattern = (body: string): RegExp => new RegExp(`^${body}$`);

const partyPattern = (suffix: string): RegExp => keyPattern(
  `party\\.(?<role>[^.]+)(?:\\.(?<index>[1-9][0-9]*))?\\.${suffix}`,
);

const ORDER_DATES = ['acceptance_date', 'contract_date', 'closing_date', 'funding_date', 'disbursement_date', 'recission_date'];
const ADDRESS_FIELDS = ['street_address', 'settlement', 'locality', 'references', 'postal_code', 'state', 'country'];
const PROPERTY_FIELDS = [
  'block', 'lot', 'section', 'range', 'township', 'subdivision', 'parcel_ids', 'legal_description',
  'brief_legal_description', 'jurisdiction', 'survey_number', 'survey_information', 'survey_date', 'type',
];
// updateCharges takes one entry per (letter, number) with its amount split by party and timing.
const CHARGE_FIELDS = ['description', 'amount', 'payee', 'buyer_at', 'buyer_before', 'seller_at', 'seller_before', 'by_others'];
const PARTY_FIELDS = ['kind', 'first_name', 'middle_name', 'last_name', 'gender', 'marital_status', 'birth', 'ssn'];
const PARTY_ADDRESS_FIELDS = ['kind', ...ADDRESS_FIELDS];
const LOAN_TERMS = [
  'terms', 'purpose', 'product', 'grace_days', 'late_payment_days', 'payment_first_date',
  'payment_last_date', 'escrow', 'demand_feature', 'amount_kind',
];

const PAYMENT_TYPES: readonly { readonly type: string; readonly required: readonly string[] }[] = [
  { type: 'wire', required: ['name', 'account', 'bank', 'bank_address', 'country'] },
  { type: 'check', required: ['name', 'account'] },
  { type: 'transfer', required: ['name', 'for_benefit_of', 'order_transfer'] },
  { type: 'holdback', required: ['name'] },
  { type: 'net_funded', required: ['name'] },
  { type: 'aggregate', required: ['payee_party_id'] },
];

const paymentRule = ({ type, required }: { readonly type: string; readonly required: readonly string[] }): CategoryRule => phase1({
  id: `party.payment.${type}`,
  keys: `party.<role>[.<n>].payment.${type}.<field>`,
  pattern: partyPattern(`payment\\.${type}\\.[A-Za-z0-9_-]+`),
  tools: ['createPartyPayment'],
  required,
  coercion: 'string',
  notes: `party_id comes from the role's party. Required for ${type}: ${required.join(', ')}`,
});

export const CATEGORY_RULES: readonly CategoryRule[] = [
  phase1({
    id: 'order.configuration',
    keys: 'order.transaction_type | order.type | order.workflow',
    pattern: keyPattern('order\\.(?:transaction_type|type|workflow)'),
    tools: ['createOrder', 'updateOrderConfiguration'],
    required: ['transaction_type', 'type', 'workflow'],
    coercion: 'enum',
    notes: 'createOrder (new order) takes plain strings from its description and needs all three; updateOrderConfiguration uses enums',
  }),
  phase1({
    id: 'order.dates',
    keys: `order.<${ORDER_DATES.join('|')}>`,
    pattern: keyPattern(`order\\.${alt(ORDER_DATES)}`),
    tools: ['updateOrderConfiguration'],
    required: [],
    coercion: 'iso-date',
    notes: 'ISO-8601 with the browser offset (see the integration rules). '
      + 'A date with no known time uses the current time of day in that offset; midnight is never a placeholder. Date-only is rejected by the tool',
  }),
  phase1({
    id: 'order.status',
    keys: 'order.status',
    pattern: keyPattern('order\\.status'),
    tools: ['updateOrderConfiguration'],
    required: [],
    coercion: 'enum',
    notes: 'closed, open, canceled or hold',
  }),
  phase1({
    id: 'order.additional_fees_letter',
    keys: 'order.additional_fees_letter',
    pattern: keyPattern('order\\.additional_fees_letter'),
    tools: ['updateOrderConfiguration'],
    required: [],
    coercion: 'enum',
    notes: 'Letter B or C',
  }),
  phase1({
    id: 'property.address',
    keys: `property.address.<${ADDRESS_FIELDS.join('|')}>`,
    pattern: keyPattern(`property\\.address\\.${alt(ADDRESS_FIELDS)}`),
    tools: ['createOrderProperty'],
    required: ['street_address', 'locality', 'postal_code'],
    coercion: 'string',
    notes: 'The tool requires every address key, so send an empty string for settlement and references when the form does not show them. '
      + 'Without street_address, locality or postal_code the address becomes an unmapped note. An existing property address cannot be changed (no tool), so such keys become notes',
  }),
  phase1({
    id: 'property.data',
    keys: `property.<${PROPERTY_FIELDS.join('|')}>`,
    pattern: keyPattern(`property\\.${alt(PROPERTY_FIELDS)}`),
    tools: ['createOrderProperty', 'updateOrderProperty'],
    required: [],
    coercion: 'string',
    notes: 'createOrderProperty for a new property; updateOrderProperty for an existing one (needs property_id from getOrder). '
      + 'jurisdiction must be Borough, City, Town, Township, Unincorporated Area or Village; type must be one of the schema values (a plan name is not a type)',
  }),
  phase1({
    id: 'party.person',
    keys: `party.<role>[.<n>].<${PARTY_FIELDS.join('|')}>`,
    pattern: partyPattern(alt(PARTY_FIELDS)),
    tools: ['searchParty', 'createParty', 'updateParty', 'createOrderAssociation'],
    required: ['kind'],
    coercion: 'string',
    notes: 'searchParty first, then createParty or updateParty, then createOrderAssociation with kind=<role>. '
      + 'kind must be a full allowed value (e.g. Limited Liability Company (LLC), never LLC). '
      + 'Entity names (no first/last) are unsupported: unmapped note',
  }),
  phase1({
    id: 'party.address',
    keys: `party.<role>[.<n>].address.<${PARTY_ADDRESS_FIELDS.join('|')}>`,
    pattern: partyPattern(`address\\.${alt(PARTY_ADDRESS_FIELDS)}`),
    tools: ['createPartyAddress'],
    required: ['kind'],
    coercion: 'string',
    notes: 'kind is one of Mailing, Home, Work, Other',
  }),
  phase1({
    id: 'party.phone',
    keys: 'party.<role>[.<n>].phone.<kind|number>',
    pattern: partyPattern('phone\\.(?:kind|number)'),
    tools: ['createPhone'],
    required: ['kind', 'number'],
    coercion: 'string',
  }),
  phase1({
    id: 'party.email',
    keys: 'party.<role>[.<n>].email.<kind|address>',
    pattern: partyPattern('email\\.(?:kind|address)'),
    tools: ['createEmail'],
    required: ['kind', 'address'],
    coercion: 'string',
    notes: 'kind and address are both required; kind is never Login',
  }),
  phase1({
    id: 'party.license',
    keys: 'party.<role>[.<n>].license.<number|state>',
    pattern: partyPattern('license\\.(?:number|state)'),
    tools: ['createLicense'],
    required: ['number', 'state'],
    coercion: 'string',
  }),
  ...PAYMENT_TYPES.map(paymentRule),
  phase1({
    id: 'loan.loan_number',
    keys: 'loan.loan_number',
    pattern: keyPattern('loan\\.loan_number'),
    tools: ['createOrderLoan'],
    required: ['loan_number'],
    coercion: 'string',
    notes: 'The loan must exist before any loan term is updated',
  }),
  phase1({
    id: 'loan.terms',
    keys: `loan.<${LOAN_TERMS.join('|')}>`,
    pattern: keyPattern(`loan\\.${alt(LOAN_TERMS)}`),
    tools: ['updateOrderLoan'],
    required: [],
    notes: 'The loan must exist. Integers for day counts, booleans for flags, ISO-8601 for payment dates',
  }),
  phase1({
    id: 'loan.interest.fixed_rate',
    keys: 'loan.interest.fixed_rate',
    pattern: keyPattern('loan\\.interest\\.fixed_rate'),
    tools: ['createOrderLoanInterest'],
    required: ['fixed_rate'],
    coercion: 'decimal-0-1',
    notes: 'Decimal between 0 and 1 (sent as data_fixed.rate); the loan must exist',
  }),
  phase1({
    id: 'charge.line',
    keys: `charge.<letter>.<number>.<${CHARGE_FIELDS.join('|')}>`,
    pattern: keyPattern(`charge\\.[A-Z]\\.[0-9]+\\.${alt(CHARGE_FIELDS)}`),
    tools: ['updateCharges'],
    required: ['description'],
    coercion: 'string',
    notes: 'letter and number are the form identity exactly as printed (HUD-1 section L, line 1101 => charge.L.1101; CD section C, line 04 => charge.C.04). '
      + 'The integration agent maps them to updateCharges letter and number under its own rules (its letter set and its recurring-letter rule). '
      + 'buyer_at/buyer_before are the borrower share at/before closing; seller_at/seller_before the seller share; by_others the share paid by others. '
      + 'payee is a company name: the agent resolves it to payee_id with searchParty. tax, months and per_month are not extracted; the agent sets them under its own rules',
  }),
  {
    id: 'amount',
    keys: 'amount.<name>',
    pattern: keyPattern('amount\\.[A-Za-z0-9_-]+'),
    tools: ['updateCharges'],
    required: [],
    phase: 2,
    coercion: 'string',
    notes: 'Any dollar amount that has no letter and number (no charge.<letter>.<number> identity) goes to the charges sub-agent, only after the order exists. '
      + 'It decides whether the amount becomes a charge or a note',
  },
  phase1({
    id: 'note',
    keys: 'note',
    pattern: keyPattern('note'),
    tools: ['createOrderNote'],
    required: [],
    coercion: 'html-note',
    notes: 'Content is HTML: the app escapes the text and wraps it in <p>; critical false unless the line says critical',
  }),
];

export const UNMAPPED_RULE: Omit<CategoryRule, 'pattern'> = {
  id: 'unmapped',
  keys: '<any other key>',
  tools: ['createOrderNote'],
  required: [],
  phase: 1,
  coercion: 'html-note',
  notes: 'Unknown key: sent as an order note with HTML escaping; reported in the result',
};

export const classifyKey = (key: string): CategoryRule | null => {
  const rule = CATEGORY_RULES.find((candidate) => candidate.pattern.test(key));
  if (!rule) return null;
  if (!rule.id.startsWith('party.')) return rule;
  const role = key.match(rule.pattern)?.groups?.role;
  return role !== undefined && normalizeRole(role) !== null ? rule : null;
};
